/* THE DEEPMARK — the writing of whoever cut the standing stones when the beasts came.
 *
 * Repurposed from `yibblese`, a constructed language and syllabary built for
 * another project (../ios-companion-app). What is borrowed is the LANGUAGE —
 * open CV syllables, one mark per syllable, a fixed alphabet learnable once.
 *
 * What is NOT borrowed is the hand. Yibblese finds its figures by search on a
 * hexagon of seven nodes, keeping five-edge shapes that are connected,
 * cyclic, self-crossing and balanced. That was reproduced here exactly, and
 * then removed: rendered into a small cell it gives 49 of 60 syllables
 * distinguishable at 7px, 52 at 11px, and 41 at 13px — it gets WORSE, because
 * the density is in the design and not in the resolution. It was drawn for
 * 115-pixel cells. See the note above the letterforms below.
 *
 * What is NOT borrowed is the tone. Yibblese is soft and warm — it was built
 * for a companion that says `helu` and `kora`. This is a dead language cut
 * into stone by whoever cut the stones when the beasts came and did not
 * come back, so the vocabulary below is theirs. The phonology is kept (open CV
 * syllables, stress on the first) because it is what makes an invented word
 * pronounceable, and a player who cannot say a word will not remember it.
 *
 * THE NAME IS ONE CONSTANT. If `deepmark` is wrong, it is this file's name
 * and the `SCRIPT_NAME` export; nothing else in the game spells it.
 *
 * Everything here is pure and deterministic: the same seed draws the same
 * inscription forever, which matters because these are carved into a world
 * the player is expected to re-enter.
 */

export const SCRIPT_NAME = 'deepmark';

/* --- THE LETTERFORMS ARE CUT FOR THE MEDIUM.
 *
 * The source syllabary draws each figure as five strokes on a seven-node
 * hexagon, with a required self-crossing, in a 115-pixel cell. That is a
 * beautiful system and it does not survive being carved at this size.
 * Measured: rendered into a 7px cell only 49 of 60 syllables come out
 * distinguishable, at 11px 52, and at 13px it gets WORSE (41) — the strokes
 * of a dense crossing figure land on each other whatever the scale, because
 * the density is in the design and not in the resolution. Reported from play,
 * twice, as the marks being unclear on the stone. They were.
 *
 * So the LANGUAGE is repurposed and the HAND is not. What is kept is
 * everything that makes it a language — open CV syllables, one mark per
 * syllable, a fixed alphabet a player can learn once — and the figures are
 * rebuilt the way every script meant for stone is built: a stem with a few
 * branches, straight cuts, no crossings. Runes and ogham look the way they do
 * because a chisel and a small surface allow nothing else, and this is the
 * same constraint arriving at the same answer.
 *
 * A glyph is a vertical stem with two or three branches. Each branch leaves
 * the stem at one of four heights and runs to one of six places, so the
 * alphabet has room for far more figures than the syllabary needs and every
 * one of them differs from every other by a whole stroke.
 */
const CELL_W = 7, CELL_H = 9;
const STEM_X = 3;

/** Where a branch may leave the stem, and where it may go. Coordinates are in
 *  a 7x9 cell; the stem runs the full height at x=3. */
const BRANCHES = [
  [1, [0, 0]], [1, [6, 0]], [1, [0, 3]], [1, [6, 3]],
  [3, [0, 1]], [3, [6, 1]], [3, [0, 5]], [3, [6, 5]],
  [5, [0, 3]], [5, [6, 3]], [5, [0, 7]], [5, [6, 7]],
  [7, [0, 5]], [7, [6, 5]], [7, [0, 8]], [7, [6, 8]],
];

/** Every two- and three-branch figure, in a fixed order so the alphabet never
 *  shifts under a player who has learned it. */
function findGlyphs() {
  const out = [];
  for (let i = 0; i < BRANCHES.length; i++) {
    for (let j = i + 1; j < BRANCHES.length; j++) {
      /* TWO BRANCHES ON THE SAME SIDE AT THE SAME HEIGHT ARE DROPPED, AND THE
       * REASON IS NOT THE ONE THAT USED TO BE WRITTEN HERE.
       *
       * It said "one stroke, not two". That is false and was measured false:
       * `(3,1)->(0,0)` and `(3,1)->(0,3)` is a V — two arms from one point —
       * and rasterised through `drawGlyph` all 8 such pairs come out
       * DISTINCT from each other and duplicate no kept figure at 7px, 9px or
       * 18px. Admitting them would make the alphabet 154, not 146.
       *
       * They stay out anyway, because 60 syllables need 146 figures and not
       * 154, and because admitting them here inserts 8 forms into the MIDDLE
       * of the enumeration — every strided index past the first would move,
       * and the paragraph above this one promises that the mark for `ka` is
       * the mark for `ka` forever. A cosmetic gain of 8 unused figures is not
       * worth respelling the script under a player who learned it.
       *
       * What they actually are is the narrowest family in the set: a V is one
       * apex and two ends, so a same-side pair sits closer to its neighbours
       * than a two-sided pair does. Keeping them out is a legibility choice,
       * which is what the rest of this file is about. */
      if (BRANCHES[i][0] === BRANCHES[j][0]
        && Math.sign(BRANCHES[i][1][0] - STEM_X) === Math.sign(BRANCHES[j][1][0] - STEM_X)) continue;
      out.push([BRANCHES[i], BRANCHES[j]]);
    }
  }
  for (let i = 0; i < BRANCHES.length; i += 3) {
    for (let j = i + 1; j < BRANCHES.length; j += 2) {
      for (let k = j + 1; k < BRANCHES.length; k += 5) {
        out.push([BRANCHES[i], BRANCHES[j], BRANCHES[k]]);
      }
    }
  }
  return out;
}

export const GLYPHS = findGlyphs();

/* --- the syllabary.
 *
 * Open syllables only, consonant-vowel or bare vowel, which is the source
 * language's rule and the reason its words can be said out loud. The
 * consonants are its softer set with the harshest dropped: this is a language
 * of people who named a lake and a hollow, not a battle. */
const CONS = ['', 'k', 'm', 'n', 'r', 's', 't', 'h', 'l', 'd', 'th', 'v'];
const VOW = ['a', 'e', 'i', 'o', 'u'];
export const SYLLABLES = [];
for (const c of CONS) for (const v of VOW) SYLLABLES.push(c + v);

/** Which figure stands for a syllable. Fixed, so the script is learnable:
 *  the mark for `ka` is the mark for `ka` in every world, forever. */
/* SPREAD ACROSS THE WHOLE ALPHABET.
 *
 * This was `i % GLYPHS.length`, which for 60 syllables over 146 figures put
 * every one of them in the first block — all two-branch forms, and not one of
 * the three-branch figures the generator makes. A lore reviewer counted it.
 * Striding the range uses both families, so the script looks like a script
 * with a range of complexity rather than one rule applied sixty times. */
const STRIDE = 7;
/* ...BUT THE ELEVEN A PLAYER ACTUALLY READS ARE CHOSEN, NOT STRIDDEN.
 *
 * `i * 7 % 146` is a stride, which is to say it is not a choice. Only 11
 * syllables are ever cut into the world — `sura keno mela rosi vako suna kote mero`,
 * every word in `WORDS` below — and a rule that never looks at the figures
 * cannot keep those 11 apart.
 *
 * MEASURED, rasterising through `drawGlyph` at size 9 and counting the pixels
 * two marks do NOT share (a figure inks 13-18 of them). Under the stride:
 *
 *   na/su 4    na/me 4    te/me 4    ra/me 4
 *
 * Four pixels of thirteen is 29%. Across the whole alphabet 450 of 10,585
 * pairs sit at 4 or under — 4.25% — and the stride landed 4 of the corpus's
 * 55 pairs there, 7.3%, on the commonest stacking there is: `na` heads 74% of
 * stones and `su` and `me` are two of the four possible feet, so the marks a
 * player compares most often were among the hardest to tell apart. The
 * alphabet is not at fault: 146 figures, 146 distinct rasters at 7px, 9px and
 * 18px alike. The eleven picked out of it were.
 *
 * A farthest-point search over the same 146 figures, restricted to the 97 not
 * already spoken for by the other 49 syllables, gives eleven with a minimum
 * pairwise distance of 9 at 9px (mean 11.9) and 7 at 7px. 9 is the ceiling: a
 * maximum-clique search at a threshold of 10 finds only 9 figures that far
 * apart, so no set of eleven does better. Before 4, after 9 — 2.25x, with no
 * new figures, no new rules and no change to the other 49 marks.
 *
 * Five two-branch forms and six three-branch, which is the range the stride
 * was reached for in the first place.
 */
const CORPUS_MARKS = new Map([
  ['na', 6], ['ko', 25], ['te', 33], ['su', 66], ['ra', 103], ['ke', 115],
  ['no', 123], ['me', 124], ['la', 130], ['ro', 135], ['si', 137],
]);
const INDEX = new Map(SYLLABLES.map((s, i) =>
  [s, CORPUS_MARKS.has(s) ? CORPUS_MARKS.get(s) : (i * STRIDE) % GLYPHS.length]));
export const glyphFor = (syl) => GLYPHS[INDEX.get(syl) ?? 0];

/** Split a word into the syllables the script can write. */
export function syllables(word) {
  const out = [];
  let i = 0;
  const w = String(word).toLowerCase();
  while (i < w.length) {
    const two = w.slice(i, i + 2), three = w.slice(i, i + 3);
    if (INDEX.has(three)) { out.push(three); i += 3; continue; }
    if (INDEX.has(two)) { out.push(two); i += 2; continue; }
    const one = w[i];
    if (INDEX.has(one)) { out.push(one); i += 1; continue; }
    i += 1;                                    // unwritable, and simply not cut
  }
  return out;
}

/* --- drawing.
 *
 * A glyph is a stem and two or three branches, cut into a 7x9 cell. Bresenham
 * rather than the canvas line API:
 * every stroke has to land on whole pixels or the marks shimmer against the
 * tile grid, which is the fault the pod's flash and the crown bake were both
 * fixed for. */
export function drawGlyph(g, glyph, x, y, size, colour) {
  const sc = size / CELL_H;
  const X = (u) => Math.round(x + u * sc);
  const Y = (v) => Math.round(y + v * sc);
  g.fillStyle = colour;
  const line = (x0, y0, x1, y1) => {
    let dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
    let err = dx + dy, cx = x0, cy = y0;
    for (let guard = 0; guard < 64; guard++) {
      g.fillRect(cx, cy, 1, 1);
      if (cx === x1 && cy === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) { err += dy; cx += sx; }
      if (e2 <= dx) { err += dx; cy += sy; }
    }
  };
  // the stem, full height
  line(X(STEM_X), Y(0), X(STEM_X), Y(CELL_H - 1));
  for (const [h, [bx, by]] of glyph) line(X(STEM_X), Y(h), X(bx), Y(by));
}

/** Write a word DOWN a column, one mark per row. Used by the stones, whose
 *  shaft is one tile wide and two tall. */
export function drawWordDown(g, word, x, y, size, colour, gap = 1) {
  const syls = syllables(word);
  syls.forEach((s, i) => drawGlyph(g, glyphFor(s), x, y + i * (size + gap), size, colour));
  return syls.length * (size + gap) - gap;
}

/** Write a word, left to right, and report how wide it was. */
export function drawWord(g, word, x, y, size, colour, gap = 2) {
  const syls = syllables(word);
  syls.forEach((s, i) => drawGlyph(g, glyphFor(s), x + i * (size + gap), y, size, colour));
  return syls.length * (size + gap) - gap;
}

/* What a player cannot see is WHERE. The lairs are three screens in a hundred and the only other thing that
 * points at them is the brief, in English. So the stone gives a bearing: the beast's place lies THAT way. */
export const WORDS = {
  /* The nouns are the beasts' places and the withheld word; the hollows' and the caches' words went with the hollows (round 43). */
  north: 'sura',
  south: 'keno',
  east: 'mela',
  west: 'rosi',
  /* A THIRD NOUN THE GAME DOES NOT CONTAIN.
   *
   * The lore lens scored `mystery` 6 and named the reason precisely: measured
   * over 1,722 stones on 60 seeds there are nine distinct sentences, every one
   * of them decodable, and the map key glosses two of the six words while the
   * map's own geometry glosses the other four. There is **no untranslated
   * word, no fragment, and no mark whose meaning the game withholds**. A
   * script a player can fully read is a script with nothing left to wonder
   * about, and a world that explains itself has no depth.
   *
   * `rako` is the only kind of mystery a decodable script can carry: it is
   * spelled from syllables the player already knows — `ra` from `sura`, `ko`
   * from `kote` — so it can be READ, sounded out, and written down. It just
   * names nothing the game contains. There is no rako to walk to, no rako in
   * the key, and nothing anywhere says what one is.
   *
   * It goes on the stone farthest from every lair: the one place with nothing to point at. When the last beast
   * falls every stone says it, and the win screen says why. */
  lair: 'vako',      // the beast's place (LS-232): what a charm stone points at now the hollows are gone
  /* EACH BEAST HAS ITS WORD (the lore lens, round 39: one noun for all three, so a stone said 'a beast's place, east'
   * and never which). Spelled from syllables already cut: su-na, ko-te, me-ro. The map key glosses each once the
   * beast has been seen, so a word read on a stone is a word learned on the map. */
  sow: 'suna',
  oldshell: 'kote',
  bramblemother: 'mero',
  unknown: 'rako',
};

/** Which way the hollow holding `charm` lies from screen (sx, sy), said in
 *  the language of whoever cut the stones.
 *
 *  Two words: the place, then the bearing. A stone beside a barrier that
 *  wants the tide points at the hollow that hands the tide over — which is
 *  the one fact about that barrier the world does not otherwise state. */
export function bearingWords(world, sx, sy, mark) {
  /* ONE SENTENCE SHAPE: the beast's word, then the bearing. `world.stones` holds a charm name, which is a
   * beast's place in the world's order; the map key teaches the grammar and a second grammar would need a
   * second key. */
  /* ...AND ONE STONE THAT IS NOT A SENTENCE AT ALL.
   *
   * `unknown` is a noun with no bearing: a word the player can read, spelled
   * from syllables they already know, naming nothing the game contains. It
   * returns before the target lookup because there IS no target — that is the
   * whole point, and a bearing would turn a question into a direction. */
  /* ...UNTIL TWO ARE DOWN (2026-09-10, LS-302). The rako stone stands farthest
   * from every lair and says the one word that names nothing the game
   * contains, and that is still all it says while there is a hunt to work out.
   * With two beasts felled there is nothing left to work out -- one lair, one
   * beast, and a player who has read the other stones already knows the
   * grammar -- so the stone that never pointed anywhere adds a bearing to the
   * last one standing. The word is untouched: it still names nothing, and
   * nothing anywhere says what a rako is. What changes is that the stone which
   * withheld everything is the one thing that speaks when you are alone with
   * the last of them. */
  if (mark === 'unknown') {
    const living = world && world.lairs && world.slain
      ? world.lairs.filter((l: any) => !world.slain.has(l.kind)) : [];
    if (!world || !world.slain || world.slain.size < 2 || living.length !== 1) return [WORDS.unknown];
    const dx = living[0].x - sx, dy = living[0].y - sy;
    if (dx === 0 && dy === 0) return [WORDS.unknown];
    return [WORDS.unknown, Math.abs(dx) >= Math.abs(dy)
      ? (dx > 0 ? WORDS.east : WORDS.west)
      : (dy > 0 ? WORDS.south : WORDS.north)];
  }
  let target = null, noun = WORDS.lair;
  if (typeof mark === 'string' && world && world.lairs && world.order) {
    /* A CHARM STONE POINTS AT A LAIR (LS-232). The stones were cut to point
     * at the hollows, one charm each; with the hollows gone every stone said
     * 'rako' -- one word naming nothing, on two thousand stones. The charm's
     * place in the world's order is a beast's place in its lairs, so a stone
     * that said 'the dug place, east' says 'the beast's place, east'. */
    const i = world.order.indexOf(mark);
    let lair = i >= 0 ? world.lairs[i % world.lairs.length] : null;
    /* ...AND FOLLOWS THE LIVING (the design lens, round 39): a stone kept pointing at a lair whose beast had fallen. When
     * the game has told the world which are slain, the stone names the next beast still standing. */
    if (lair && world.slain && world.slain.has(lair.kind)) { const living = world.lairs.filter((l) => !world.slain.has(l.kind)); lair = living.length ? living[i % living.length] : null; }
    if (lair && world.slain && world.slain.has(lair.kind)) lair = null;
    if (!lair) return [WORDS.unknown];
    target = { x: lair.x, y: lair.y };
    noun = WORDS[lair.kind] || WORDS.lair;
  } else {
    return [WORDS.unknown];
  }
  const h = target;
  if (!h) return null;
  const dx = h.x - sx, dy = h.y - sy;
  /* On the lair's own screen the stone says just the beast's word — one mark, "the
   * hollow", and nothing else. There was a `near` word here and it was the
   * redundancy trap a second time: if you are standing on the screen the
   * mouth is on, the mouth is what tells you so. It was also taught nowhere,
   * which `and every one of them is decodable from the map` caught the first
   * time it ran. */
  if (dx === 0 && dy === 0) return [noun];
  const dir = Math.abs(dx) >= Math.abs(dy)
    ? (dx > 0 ? WORDS.east : WORDS.west)
    : (dy > 0 ? WORDS.south : WORDS.north);
  return [noun, dir];
}

/** How wide an inscription of these words will be, in pixels. */
export function inscriptionWidth(words, size, gap = 2, space = 4) {
  return words.reduce((w, word, i) =>
    w + syllables(word).length * (size + gap) - gap + (i ? space : 0), 0);
}
